import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import { Resources } from "Infrastructure/Translations/Resources";
import { isNumber } from "lodash-es";
import type { ObjPathProxy } from "ts-object-path";
import { number, string } from "yup";

export type AssetAllocationFields = {
  readonly equities?: number;
  readonly equitiesName?: string;
  readonly bonds?: number;
  readonly bondsName?: string;
  readonly commodities?: number;
  readonly commoditiesName?: string;
  readonly realEstate?: number;
  readonly realEstateName?: string;
  readonly cash?: number;
  readonly cashName?: string;
  readonly other?: number;
  readonly otherName?: string;
};

const createAssetAllocationTest = (required?: boolean) => {
  return function (this: any) {
    const formData = this.parent;
    if (!formData) return true;

    const { equities, bonds, commodities, realEstate, cash, other } = formData;

    const hasAnyNonZero =
      (isNumber(equities) && equities !== 0) ||
      (isNumber(bonds) && bonds !== 0) ||
      (isNumber(commodities) && commodities !== 0) ||
      (isNumber(realEstate) && realEstate !== 0) ||
      (isNumber(cash) && cash !== 0) ||
      (isNumber(other) && other !== 0);

    // When not required, all-zero defaults should not trigger sum validation
    if (!required && !hasAnyNonZero) return true;

    const hasAnyAllocation =
      isNumber(equities) || isNumber(bonds) || isNumber(commodities) || isNumber(realEstate) || isNumber(cash) || isNumber(other);

    if (hasAnyAllocation) {
      const sum = (equities ?? 0) + (bonds ?? 0) + (commodities ?? 0) + (realEstate ?? 0) + (cash ?? 0) + (other ?? 0);
      if (sum !== 100) {
        return this.createError({ message: "" });
      }
    }
    return true;
  };
};

export const createAssetAllocationField = (
  t: (resourcePath: ObjPathProxy<ResourceDictionary, string>, options?: any) => string,
  required?: boolean,
) => {
  const base = number().optional().test("asset-allocation-sum", "", createAssetAllocationTest(required));
  return required ? base.required(t(Resources.Validation.Required)) : base;
};

export const createAssetAllocationSchema = (
  t: (resourcePath: ObjPathProxy<ResourceDictionary, string>, options?: any) => string,
  required?: boolean,
) => {
  return {
    equities: createAssetAllocationField(t, required),
    equitiesName: string().optional(),
    bonds: createAssetAllocationField(t, required),
    bondsName: string().optional(),
    commodities: createAssetAllocationField(t, required),
    commoditiesName: string().optional(),
    realEstate: createAssetAllocationField(t, required),
    realEstateName: string().optional(),
    cash: createAssetAllocationField(t, required),
    cashName: string().optional(),
    other: createAssetAllocationField(t, required),
    otherName: string().optional(),
  };
};
